import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { StoreDTO, CreateStoreDTO, StoreState, StorePaginationResponse } from '../../types/store';
import { createStoreAPI, deleteStoreAPI, fetchStoresAPI, updateStoreAPI } from './storeAPI';
import { RootState } from '../../app/store';

const initialState: StoreState = {
  items: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 0,
  totalItems: 0,
  itemsPerPage: 10,
  hasNext: false,
  hasPrev: false
};

export const fetchStores = createAsyncThunk(
  'store/fetchStores',
  async ({ page, limit, search }: { page?: number; limit?: number; search?: string }, { rejectWithValue }) => {
    try {
      const response = await fetchStoresAPI(page, limit, search);
      if (!response.status) {
        return rejectWithValue(response);
      }
      
      if (!response.data) {
        return rejectWithValue({ status: false, message: 'Failed to fetch stores' });
      }
      
      // Return complete response with both data and pagination
      return { 
        response: { 
          data: response.data, 
          pagination: response.pagination 
        }, 
        requestedLimit: limit 
      }; 
    } catch (error) {
      return rejectWithValue({ status: false, message: 'Network error occurred' });
    }
  }
);

export const createStore = createAsyncThunk(
  'store/createStore',
  async ({ store, userId }: { store: CreateStoreDTO; userId: string }, { rejectWithValue }) => {
    const response = await createStoreAPI(store, userId);
    if (!response.status) {
      return rejectWithValue(response);
    }
    return response.data;
  }
);

export const updateStore = createAsyncThunk(
  'store/updateStore',
  async ({ uuid, store }: { uuid: string; store: Partial<CreateStoreDTO> }, { rejectWithValue }) => {
    const response = await updateStoreAPI(uuid, store);
    if (!response.status) {
      return rejectWithValue(response);
    }
    return response.data;
  }
);

export const deleteStore = createAsyncThunk(
  'store/deleteStore',
  async (uuid: string, { rejectWithValue }) => {
    const response = await deleteStoreAPI(uuid);
    if (!response.status) {
      return rejectWithValue(response);
    }
    return uuid;
  }
);

const storeSlice = createSlice({
  name: 'store',
  initialState,
  reducers: {
    resetStore: (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
      state.currentPage = 1;
      state.totalPages = 0;
      state.totalItems = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch stores
      .addCase(fetchStores.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        if (action.payload) {
          state.loading = false;
          state.error = null;
          
          const { response, requestedLimit } = action.payload as any;
          
          if (response && response.data && Array.isArray(response.data) && response.pagination) {
            // New format: { data: [...], pagination: {...} }
            state.items = response.data;
            state.currentPage = response.pagination.currentPage;
            state.totalPages = response.pagination.totalPages;
            state.totalItems = response.pagination.totalItems;
            state.itemsPerPage = requestedLimit || response.pagination.pageSize;
            state.hasNext = response.pagination.hasNext;
            state.hasPrev = response.pagination.hasPrev;
          } else if (Array.isArray(response)) {
            // Fallback: direct array
            state.items = response;
            state.currentPage = 1;
            state.totalPages = 1;
            state.totalItems = response.length;
            state.itemsPerPage = requestedLimit || 10;
            state.hasNext = false;
            state.hasPrev = false;
          } else {
            state.items = [];
          }
        }
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const errorResponse = action.payload as any;
          state.error = errorResponse.message || 'Failed to fetch stores';
        } else {
          state.error = action.error.message ?? 'Failed to fetch stores';
        }
      })
      // Create store
      .addCase(createStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStore.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(createStore.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const errorResponse = action.payload as any;
          state.error = errorResponse.message || 'Failed to create store';
        } else {
          state.error = action.error.message ?? 'Failed to create store';
        }
      })
      // Update store
      .addCase(updateStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStore.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload) {
          const updatedStore = action.payload as StoreDTO;
          state.items = state.items.map(item => 
            item.uuid === updatedStore.uuid ? updatedStore : item
          );
        }
      })
      .addCase(updateStore.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const errorResponse = action.payload as any;
          state.error = errorResponse.message || 'Failed to update store';
        } else {
          state.error = action.error.message ?? 'Failed to update store';
        }
      })
      // Delete store
      .addCase(deleteStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStore.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload) {
          state.items = state.items.filter(item => item.uuid !== action.payload);
        }
      })
      .addCase(deleteStore.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const errorResponse = action.payload as any;
          state.error = errorResponse.message || 'Failed to delete store';
        } else {
          state.error = action.error.message ?? 'Failed to delete store';
        }
      });
  }
});

export const { resetStore } = storeSlice.actions;

export const selectStores = (state: RootState) => state.store.items;
export const selectStoreLoading = (state: RootState) => state.store.loading;
export const selectStoreError = (state: RootState) => state.store.error;
export const selectStorePagination = (state: RootState) => ({
  currentPage: state.store.currentPage,
  totalPages: state.store.totalPages,
  totalItems: state.store.totalItems,
  itemsPerPage: state.store.itemsPerPage,
  hasNext: state.store.hasNext,
  hasPrev: state.store.hasPrev,
});

export default storeSlice.reducer;
